/*
 * Shared custom-select listbox. Six consumers: product-quick-view-main-state,
 * product-recharge-select, product-reviews, mini-bag-subscription-row,
 * product-custom-bundle-picker, blocks/cms-rate-table-group.
 *
 * Contract every consumer's `[data-custom-select-wrap]` markup must meet:
 * - `wrap` must itself be a positioned element (`relative` or `absolute`) —
 *   it's the containing block the open list is anchored against (or, in a
 *   drawer, the anchor `anchor()` measures wrap's own rect relative to).
 * - No ancestor between `wrap` and the viewport may establish a clip or
 *   scroll container. This includes indirectly — e.g. `overflow-x: hidden`
 *   promotes a computed `overflow-y: visible` to `auto` per the CSS Overflow
 *   spec, which is enough to turn an unrelated ancestor into a scroll
 *   container that clips the list. The list no longer escapes this the way
 *   the old body-portaled `fixed` version did, in exchange for never
 *   needing to track scroll to reposition itself (TASK-53117).
 */
(function () {
  if (window.CustomSelect) return;

  var uid = 0;
  var MAX_VISIBLE_OPTIONS = 5;

  function optionEls(list) {
    return Array.prototype.slice.call(list.querySelectorAll('[role="option"]:not([aria-disabled])'));
  }

  /* Combines a context's own clamp (room available, or null for none — the
     Recharge branch) with the row cap buildList() measured, and writes
     whichever is smaller. */
  function setMaxHeight(list, roomClamp) {
    var rowCap = list.__rowCapHeight;
    var value = roomClamp === null ? rowCap : (rowCap === null ? roomClamp : Math.min(roomClamp, rowCap));
    list.style.maxHeight = value === null ? '' : Math.max(value, 0) + 'px';
  }

  function positionAgainstWrap(list, wrap) {
    var wrapRect = wrap.getBoundingClientRect();
    var wrapStyle = getComputedStyle(wrap);
    var borderLeft = parseFloat(wrapStyle.borderLeftWidth) || 0;
    var borderTop = parseFloat(wrapStyle.borderTopWidth) || 0;
    list.style.top = (wrapRect.height - borderTop) + 'px';
    list.style.left = -borderLeft + 'px';
    list.style.width = wrapRect.width + 'px';
    return wrapRect;
  }

  function buildList(select, parent, scrollable) {
    var list = document.createElement('ul');
    list.setAttribute('role', 'listbox');
    list.className =
      'absolute z-select-list ' +
      (scrollable ? 'overflow-y-auto scrollbar-filters ' : 'overflow-hidden ') +
      'border-b border-l border-r border-border-secondary bg-surface-primary';
    var listId = (select.id || 'custom-select-' + (uid += 1)) + '-listbox';
    list.id = listId;

    Array.prototype.forEach.call(select.options, function (option, index) {
      var li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', option.value);
      li.id = listId + '-option-' + index;
      li.className =
        'flex cursor-pointer items-center gap-12 whitespace-nowrap border-b border-border-secondary bg-surface-primary px-20 py-16 text-b2 text-primary last:border-b-0 hover:bg-black/5 data-active:bg-black/5 aria-selected:bg-surface-blue aria-selected:text-invert aria-selected:hover:bg-surface-blue aria-selected:data-active:bg-surface-blue';
      li.textContent = option.textContent.trim();
      if (option.disabled) li.setAttribute('aria-disabled', 'true');
      list.appendChild(li);
    });

    parent.appendChild(list);
    /* Measured post-append, before anything hides the list — anchor() runs
       later while it's display:none, where this would just read 0. Guarded
       against a 0 reading anyway (a hidden-at-measurement-time subtree),
       since that would otherwise cap the list to max-height: 0. Includes the
       list's own bottom border so the 5th row's border isn't clipped by it
       (max-height applies to the border box). */
    var firstRowHeight = list.children.length > MAX_VISIBLE_OPTIONS
      ? list.children[0].getBoundingClientRect().height
      : 0;
    list.__rowCapHeight = firstRowHeight > 0
      ? firstRowHeight * MAX_VISIBLE_OPTIONS + (parseFloat(getComputedStyle(list).borderBottomWidth) || 0)
      : null;
    return list;
  }

  function enhance(wrap, externalSelect) {
    if (wrap.__customSelectEnhanced) return;
    var select = externalSelect || wrap.querySelector('select');
    if (!select) return;
    wrap.__customSelectEnhanced = true;

    var drawerContent = wrap.closest('[data-drawer-content]');
    /* Also the hook assets/analytics-subscription.js uses to identify this
       specific control for its subscription_frequency event — routes both
       an unrelated analytics concern and this positioning branch. */
    var isRechargeOwned = wrap.hasAttribute('data-recharge-frequency');
    /* Drawer and plain page-level lists always get a scrollbar: both can
       apply a room-based max-height clamp regardless of option count, and a
       clamped-but-not-scrollable list would silently hide unreachable
       options with no visual cue. Recharge has no room clamp (its wrap is
       positioned by its own owner file) — overflow-hidden is inert there
       unless the row cap below applies. */
    function computeNeedsScrollbar() {
      return !isRechargeOwned || select.options.length > MAX_VISIBLE_OPTIONS;
    }
    var list = buildList(select, drawerContent || wrap, computeNeedsScrollbar());
    var activeIndex = -1;

    wrap.setAttribute('role', 'combobox');
    wrap.setAttribute('aria-haspopup', 'listbox');
    wrap.setAttribute('aria-expanded', 'false');
    wrap.setAttribute('aria-controls', list.id);
    if (!wrap.hasAttribute('tabindex')) wrap.setAttribute('tabindex', select.disabled ? '-1' : '0');

    function syncSelectedFromSelect() {
      var current = select.options[select.selectedIndex];
      Array.prototype.forEach.call(list.querySelectorAll('[role="option"]'), function (li) {
        li.setAttribute('aria-selected', current && li.getAttribute('data-value') === current.value ? 'true' : 'false');
      });
      var label = wrap.querySelector('[data-custom-select-label]');
      if (label) label.textContent = current ? current.textContent.trim() : '';
    }

    function isOpen() {
      return list.style.display !== 'none';
    }

    function anchor() {
      if (drawerContent) {
        if (getComputedStyle(drawerContent).position === 'static') {
          drawerContent.style.position = 'relative';
        }
        var wrapRect = wrap.getBoundingClientRect();
        var scrollerRect = drawerContent.getBoundingClientRect();
        var top = (wrapRect.bottom - scrollerRect.top) + drawerContent.scrollTop;
        var left = (wrapRect.left - scrollerRect.left) + drawerContent.scrollLeft;
        list.style.top = top + 'px';
        list.style.left = left + 'px';
        list.style.width = wrapRect.width + 'px';
        setMaxHeight(list, Math.max(drawerContent.scrollHeight - top, 0));
        return;
      }
      /* product-recharge-select.liquid positions `wrap` itself (see that
         file); nothing to compute for position here. The row cap still
         needs applying though, independent of position. */
      if (isRechargeOwned) {
        positionAgainstWrap(list, wrap);
        setMaxHeight(list, null);
        return;
      }
      /* Plain page-level trigger (e.g. the reviews rating filter, the D&R
         rate switcher): wrap moves with the page natively, so position
         needs no tracking — only the max-height clamp does, against
         whichever sticky add-to-bag bar is showing below it, same
         measured-not-guessed approach as the drawer branch (D80).

         Known gap, not yet handled: this only clamps the BOTTOM edge. A
         list opening upward into the fixed header or the desktop sticky ATB
         (both above the trigger) can still paint over them — a downward-
         opening list can't be fixed by a bottom-only clamp; it needs either
         a flip-to-open-upward or a top-side clamp with repositioning. Left
         unimplemented rather than guessed at without a way to verify it live. */
      var wrapRect2 = positionAgainstWrap(list, wrap);
      var bottom = window.innerHeight;
      var bottomBar = document.querySelector('[data-sticky-atb-mobile][data-visible="true"]');
      var bottomBarRect = bottomBar && bottomBar.getBoundingClientRect();
      if (bottomBarRect && bottomBarRect.height) bottom = Math.min(bottom, bottomBarRect.top);
      setMaxHeight(list, Math.max(bottom - wrapRect2.bottom, 0));
    }

    function setActive(index) {
      var opts = optionEls(list);
      if (!opts.length) return;
      activeIndex = ((index % opts.length) + opts.length) % opts.length;
      opts.forEach(function (li) { li.removeAttribute('data-active'); });
      var active = opts[activeIndex];
      active.setAttribute('data-active', '');
      wrap.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    function open() {
      if (select.disabled || isOpen()) return;
      syncSelectedFromSelect();
      anchor();
      list.style.removeProperty('display');
      wrap.setAttribute('aria-expanded', 'true');
      var opts = optionEls(list);
      var selectedLi = opts.filter(function (li) { return li.getAttribute('aria-selected') === 'true'; })[0];
      setActive(selectedLi ? opts.indexOf(selectedLi) : 0);
    }

    function close(focusTrigger) {
      if (!isOpen()) return;
      list.style.display = 'none';
      wrap.setAttribute('aria-expanded', 'false');
      wrap.removeAttribute('aria-activedescendant');
      if (focusTrigger) wrap.focus();
    }

    function choose(li) {
      if (!li || li.hasAttribute('aria-disabled')) return;
      var value = li.getAttribute('data-value');
      if (select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      syncSelectedFromSelect();
      close(true);
    }

    list.style.display = 'none';
    syncSelectedFromSelect();

    wrap.addEventListener('click', function (event) {
      /* In the Recharge and page-level branches the list is a DOM child of
         wrap, so clicks inside it (an option, or its own scrollbar) bubble
         through here too — without this guard the list closes on its own
         scrollbar click and choose()'s close(true) never reaches
         wrap.focus(), since this handler's close(false) already ran first. */
      if (list.contains(event.target)) return;
      if (isOpen()) close(false); else open();
    });

    wrap.addEventListener('keydown', function (event) {
      var opts = optionEls(list);
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen()) open(); else setActive(activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen()) open(); else setActive(activeIndex - 1);
          break;
        case 'Home':
          if (isOpen()) { event.preventDefault(); setActive(0); }
          break;
        case 'End':
          if (isOpen()) { event.preventDefault(); setActive(opts.length - 1); }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (isOpen()) choose(opts[activeIndex]); else open();
          break;
        case 'Escape':
          if (isOpen()) { event.preventDefault(); close(true); }
          break;
        case 'Tab':
          close(false);
          break;
      }
    });

    document.addEventListener('click', function (event) {
      var option = event.target.closest('[role="option"]');
      if (option && list.contains(option)) {
        choose(option);
        return;
      }
      if (!isOpen() || wrap.contains(event.target) || list.contains(event.target)) return;
      close(false);
    });

    var anchorRaf = null;
    function scheduleAnchor() {
      if (!isOpen() || anchorRaf) return;
      anchorRaf = requestAnimationFrame(function () {
        anchorRaf = null;
        if (isOpen()) anchor();
      });
    }

    if (!isRechargeOwned) {
      window.addEventListener('resize', scheduleAnchor, { passive: true });
    }
    if (!drawerContent && !isRechargeOwned) {
      window.addEventListener('scroll', scheduleAnchor, { passive: true, capture: true });
    }

    new MutationObserver(function () {
      if (wrap.__customSelectRebuilding) return;
      wrap.__customSelectRebuilding = true;
      var wasOpen = isOpen();
      list.remove();
      list = buildList(select, drawerContent || wrap, computeNeedsScrollbar());
      wrap.setAttribute('aria-controls', list.id);
      syncSelectedFromSelect();
      if (wasOpen) {
        anchor();
        /* aria-controls alone leaves aria-activedescendant pointing at the
           just-removed element — re-derive it the same way open() does,
           rather than leave a stale IDREF on the combobox while it's open. */
        var opts = optionEls(list);
        if (opts.length) {
          var selectedLi = opts.filter(function (li) { return li.getAttribute('aria-selected') === 'true'; })[0];
          setActive(selectedLi ? opts.indexOf(selectedLi) : 0);
        } else {
          wrap.removeAttribute('aria-activedescendant');
        }
      } else {
        list.style.display = 'none';
      }
      wrap.__customSelectRebuilding = false;
    }).observe(select, { childList: true });

    new MutationObserver(function () {
      var disabled = select.disabled;
      wrap.setAttribute('tabindex', disabled ? '-1' : '0');
      if (disabled) close(false);
    }).observe(select, { attributes: true, attributeFilter: ['disabled'] });
  }

  function enhanceAll(root) {
    (root || document).querySelectorAll('[data-custom-select-wrap]').forEach(function (wrap) {
      enhance(wrap);
    });
  }

  window.CustomSelect = { enhance: enhance, enhanceAll: enhanceAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { enhanceAll(document); });
  } else {
    enhanceAll(document);
  }

  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-custom-select-wrap]')) enhance(node);
          if (node.querySelectorAll) enhanceAll(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
